/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import React, {FC} from 'react'
import {Routes, Route, BrowserRouter, Navigate} from 'react-router-dom'
import {PrivateRoutes} from './PrivateRoutes'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {Logout, AuthPage, useAuth} from '../modules/auth'
import {App, NoAuthApp} from '../App'
import {HasPermissionInGroup, HasPermissionInRole} from 'app/shared/Common/Util'
import {Group, Role} from 'app/shared/Common/Enums'
import {PrivacyPolicy} from 'app/modules/root-pages/privacy-policy'
import {TermsConditions} from 'app/modules/root-pages/terms-conditions'

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const {PUBLIC_URL} = process.env

const AppRoutes: FC = () => {
  const {currentUser} = useAuth()
  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <Route path='logout' element={<Logout />} />
          {currentUser ? (
            <>
              <Route path='/*' element={<PrivateRoutes />} />
              {HasPermissionInRole(currentUser, [Role.EXT_Report_Full, Role.EXT_Report_Read]) &&
              HasPermissionInGroup(currentUser, [Group.Member_User_Administrator, Group.Member_Normal_User, Group.Member_Stakeholder]) ? (
                <Route index element={<Navigate to='/dashboard' />} />
              ) : (
                <Route index element={<Navigate to='/pledge' />} />
              )}
            </>
          ) : (
            <>
              <Route path='auth/*' element={<AuthPage />} />
              <Route path='*' element={<Navigate to='/auth' replace />} />
            </>
          )}
        </Route>
        <Route element={<NoAuthApp/>}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='terms-conditions' element={<TermsConditions />} />
          <Route path='privacy-policy' element={<PrivacyPolicy />} />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
